/**
 * A biblioteca vista pelo administrador.
 *
 * Duas coisas acontecem aqui, e as duas são o trabalho de quem cuida do site:
 *
 * 1. **Moderação** — cada trabalho enviado fica *em análise* até alguém ler. A
 *    tela mostra a declaração de uso de IA e a ficha de indícios (referências,
 *    figuras, seções...) para a decisão não ser só "no escuro". O sistema não
 *    afirma se o texto é de IA: ele mostra o que conta.
 * 2. **Repasse** — o dinheiro das consultas cai todo na conta da plataforma. Este
 *    é o relatório que fecha o mês com cada autor; o Pix é feito fora do sistema
 *    e o botão só marca o que já foi transferido.
 */

import { useCallback, useEffect, useState } from 'react';
import { Banknote, Check, Loader2, ShieldCheck, TriangleAlert, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api, ErroApi } from '@/lib/api';
import {
  ROTULO_ESTADO_DA_PUBLICACAO,
  ROTULO_NIVEL_DA_BIBLIOTECA,
  ROTULO_USO_DE_IA,
  linhasDosIndicios,
  precoFormatado,
  type AutorDoRepasse,
  type PublicacaoDoPainel,
} from '@/lib/biblioteca';

const FILTROS = [
  ['EM_ANALISE', 'Em análise'],
  ['PUBLICADA', 'Publicadas'],
  ['RECUSADA', 'Recusadas'],
  ['ARQUIVADA', 'Arquivadas'],
] as const;

export function BibliotecaDoPainel() {
  const [aba, setAba] = useState<'MODERACAO' | 'REPASSES'>('MODERACAO');
  const [estado, setEstado] = useState<string>('EM_ANALISE');
  const [publicacoes, setPublicacoes] = useState<PublicacaoDoPainel[] | null>(null);
  const [autores, setAutores] = useState<AutorDoRepasse[] | null>(null);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState('');

  const carregar = useCallback(async () => {
    setPublicacoes(null);
    try {
      const { publicacoes: lista } = await api.bibliotecaDoPainel(estado);
      setPublicacoes(lista);
      setErro('');
    } catch (causa) {
      setPublicacoes([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar a biblioteca.');
    }
  }, [estado]);

  const carregarRepasses = useCallback(async () => {
    setAutores(null);
    try {
      const { autores: lista } = await api.repassesDaBiblioteca();
      setAutores(lista);
      setErro('');
    } catch (causa) {
      setAutores([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar os repasses.');
    }
  }, []);

  useEffect(() => {
    if (aba === 'MODERACAO') void carregar();
    else void carregarRepasses();
  }, [aba, carregar, carregarRepasses]);

  const moderar = async (id: string, dados: Parameters<typeof api.moderarPublicacao>[1]) => {
    setOcupado(id);
    try {
      await api.moderarPublicacao(id, dados);
      await carregar();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível atualizar a publicação.');
    } finally {
      setOcupado('');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {(
          [
            ['MODERACAO', 'Moderação'],
            ['REPASSES', 'Repasses aos autores'],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            onClick={() => setAba(valor)}
            className={
              aba === valor
                ? 'rounded-full border border-primary bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary'
                : 'rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-primary/40'
            }
          >
            {rotulo}
          </button>
        ))}
      </div>

      {erro ? (
        <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {aba === 'MODERACAO' ? (
        <>
          <div className="flex flex-wrap gap-2">
            {FILTROS.map(([valor, rotulo]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setEstado(valor)}
                className={
                  estado === valor
                    ? 'rounded-md border border-primary bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary'
                    : 'rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:border-primary/40'
                }
              >
                {rotulo}
              </button>
            ))}
          </div>

          {publicacoes === null ? (
            <div className="flex min-h-[20vh] items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span className="text-sm">Carregando...</span>
            </div>
          ) : publicacoes.length === 0 ? (
            <p className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
              Nada por aqui neste estado.
            </p>
          ) : (
            <div className="space-y-3">
              {publicacoes.map((publicacao) => (
                <Card key={publicacao.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-center gap-2 pb-1">
                      <Badge tom={publicacao.estado === 'PUBLICADA' ? 'sucesso' : 'neutro'}>
                        {ROTULO_ESTADO_DA_PUBLICACAO[publicacao.estado]}
                      </Badge>
                      <Badge tom="principal">{ROTULO_NIVEL_DA_BIBLIOTECA[publicacao.nivel].split(' (')[0]}</Badge>
                      <span className="text-[11px] text-muted-foreground">
                        {publicacao.gratuito ? 'Sem cobrança' : precoFormatado(publicacao.precoCentavos)}
                      </span>
                    </div>
                    <CardTitle className="text-base">{publicacao.titulo}</CardTitle>
                    <CardDescription>
                      {publicacao.autores} · {publicacao.instituicao}
                      {publicacao.curso ? ` · ${publicacao.curso}` : ''} · {publicacao.ano}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">{publicacao.resumo}</p>

                    <div className="grid gap-2 rounded-md border border-border/60 bg-muted/30 p-2 text-[11px] sm:grid-cols-2">
                      <p>
                        <strong>Declaração de IA:</strong> {ROTULO_USO_DE_IA[publicacao.usaIA]}
                      </p>
                      <p>
                        <strong>Autor da conta:</strong> {publicacao.autor.nome} ({publicacao.autor.email})
                      </p>
                      {publicacao.comoUsouIA ? (
                        <p className="sm:col-span-2">
                          <strong>Como usou:</strong> {publicacao.comoUsouIA}
                        </p>
                      ) : null}
                      {publicacao.indicios ? (
                        <ul className="sm:col-span-2 list-disc pl-4 leading-relaxed">
                          {linhasDosIndicios(publicacao.indicios).map((linha) => (
                            <li key={linha}>{linha}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                      {publicacao.vendas.consultas} consulta(s) paga(s) ·{' '}
                      {precoFormatado(publicacao.vendas.totalCentavos ?? 0)} no total ·{' '}
                      {precoFormatado(publicacao.vendas.aRepassarCentavos ?? 0)} a repassar
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {publicacao.estado !== 'PUBLICADA' ? (
                        <Button
                          tamanho="pequeno"
                          onClick={() => void moderar(publicacao.id, { estado: 'PUBLICADA' })}
                          disabled={ocupado === publicacao.id}
                        >
                          <ShieldCheck />
                          Aprovar e publicar
                        </Button>
                      ) : null}
                      <Button
                        variant="contorno"
                        tamanho="pequeno"
                        onClick={() => void moderar(publicacao.id, { gratuito: !publicacao.gratuito })}
                        disabled={ocupado === publicacao.id}
                      >
                        <Banknote />
                        {publicacao.gratuito ? 'Voltar a cobrar' : 'Deixar sem cobrança'}
                      </Button>
                      {publicacao.estado !== 'RECUSADA' ? (
                        <Button
                          variant="fantasma"
                          tamanho="pequeno"
                          onClick={() => void moderar(publicacao.id, { estado: 'RECUSADA' })}
                          disabled={ocupado === publicacao.id}
                        >
                          <X />
                          Recusar
                        </Button>
                      ) : null}
                      {publicacao.estado !== 'ARQUIVADA' ? (
                        <Button
                          variant="fantasma"
                          tamanho="pequeno"
                          onClick={() => void moderar(publicacao.id, { estado: 'ARQUIVADA' })}
                          disabled={ocupado === publicacao.id}
                        >
                          Arquivar
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
            O valor das consultas cai na conta da plataforma. Faça o Pix para o autor e clique em
            <strong className="text-foreground"> Marcar como repassado</strong> — o valor continua no
            histórico, muda só a coluna.
          </p>

          {autores === null ? (
            <div className="flex min-h-[20vh] items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span className="text-sm">Carregando...</span>
            </div>
          ) : autores.length === 0 ? (
            <p className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
              Nenhuma consulta paga ainda.
            </p>
          ) : (
            <div className="space-y-3">
              {autores.map((item) => (
                <Card key={item.autor.id}>
                  <CardHeader>
                    <CardTitle className="text-base">{item.autor.nome}</CardTitle>
                    <CardDescription>
                      {item.autor.email}
                      {item.autor.telefone ? ` · ${item.autor.telefone}` : ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      {item.consultas} consulta(s) · {precoFormatado(item.totalCentavos)} no total ·{' '}
                      <strong className="text-foreground">{precoFormatado(item.aRepassarCentavos)} a repassar</strong>
                    </p>
                    <ul className="space-y-1 text-[11px] text-muted-foreground">
                      {item.pendencias.map((pendencia) => (
                        <li key={pendencia.consultaId} className="flex items-center justify-between gap-2">
                          <span className="truncate">{pendencia.titulo}</span>
                          <span className="shrink-0">
                            {precoFormatado(pendencia.valorCentavos)}
                            {pendencia.repassadoEm ? (
                              <span className="ml-1 inline-flex items-center gap-0.5 text-success">
                                <Check className="size-3" /> repassado
                              </span>
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      tamanho="pequeno"
                      onClick={async () => {
                        setOcupado(item.autor.id);
                        try {
                          await api.marcarRepasse(item.autor.id);
                          await carregarRepasses();
                        } catch (causa) {
                          setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível marcar o repasse.');
                        } finally {
                          setOcupado('');
                        }
                      }}
                      disabled={ocupado === item.autor.id || item.aRepassarCentavos === 0}
                    >
                      {ocupado === item.autor.id ? <Loader2 className="animate-spin" /> : <Banknote />}
                      Marcar como repassado
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
